import { getToken, getUserDetails } from '../admin/helper/SessionHelper'

/**
 * Who is logged in and what they are allowed to see (T-018).
 *
 * Everything here is defensive, because it runs on the logged-out path. The
 * fault this replaces: MasterLayout called
 *
 *     getUserDetails()['permissions'].includes(...)
 *
 * with no check that anyone was logged in. getUserDetails() reads localStorage
 * and JSON.parses it, so with no session it returns null and that line throws a
 * TypeError. A deep link to any authenticated screen while logged out — a
 * bookmark, a shared URL, a session that expired overnight with the tab still
 * open — blanked the app with a console error instead of showing the login
 * form.
 */

export function isLoggedIn() {
    const token = getToken()
    // localStorage.getItem returns null for a missing key, and the string
    // "null" if something ever wrote null into it.
    return Boolean(token) && token !== 'null' && token !== 'undefined'
}

export function getPermissions() {
    try {
        const details = getUserDetails()
        const permissions = details?.permissions
        return Array.isArray(permissions) ? permissions : []
    } catch (e) {
        // getUserDetails JSON.parses whatever is in localStorage. Corrupt
        // storage — a half-written value, a manual edit — must not take the
        // whole app down.
        return []
    }
}

/**
 * Does this user hold `permission`?
 *
 * ⚠️ DENY BY DEFAULT. This used to fail OPEN in two separate ways and both are
 * now closed. The client's rule, stated 31 Jul 2026:
 *
 *     "if permission is not given cant see or do anything"
 *
 * WHAT WAS REMOVED, AND WHY IT WAS THERE
 *
 * 1. The unknown-resource rule. If not one of the user's permissions mentioned
 *    a resource, access was ALLOWED — on the reasoning that the names in
 *    permissions.js were guesses, and that wrongly hiding a screen from the
 *    owner costs a support call while wrongly showing one is refused by the API
 *    anyway.
 *
 *    That was a fair trade while the names WERE guesses. It stopped being one
 *    when the R-1 pass gave every screen its real key, mirrored from
 *    API_REFERENCE §16 — which is the exit condition the rule set for itself:
 *    "correct the names in permissions.js and this rule stops firing".
 *
 *    It has been harmless only because every user is a Super Admin holding
 *    every permission, and that is the exact condition per-shop roles end. For
 *    a Cashier, "resource absent from my list" is the NORMAL case — precisely
 *    what the rule allowed.
 *
 * 2. An empty permission array returned true, so a user allowed to do nothing
 *    could see everything. "Nothing" is now a real state: a user with no role
 *    in the shop they picked. The branch existed to stop the UI blanking
 *    mid-shop-switch, and ShopPicker now fetches the new permissions BEFORE
 *    reloading, so that window is gone.
 *
 * The backend remains the real boundary — it checks every request, per shop.
 * This decides what to OFFER. Offering a door that will not open is a bug
 * report; hiding one the user is entitled to is a support call. Both are worse
 * than being right, which is now achievable.
 */
export function hasPermission(permission) {
    // Screens that require no permission. That set is closed and listed in
    // AUTHENTICATED_BUT_UNGATED, with a test asserting every route in the
    // router is either mapped or deliberately listed there — so "no entry in
    // the map" can never quietly mean "open to everyone".
    if (!permission) return true

    return getPermissions().includes(permission)
}

/**
 * Where the user was heading before being sent to the login screen.
 *
 * sessionStorage rather than router state, because logging in navigates with
 * window.location.href — a full page load, which throws router state away.
 * Cleared on read so it cannot resurface on a later, unrelated login.
 */
const INTENDED = 'intendedPath'

export function rememberIntendedPath(path) {
    // Never send someone back to the login screen itself, and never to an
    // absolute URL somebody put in the address bar.
    if (!path || !path.startsWith('/') || path.startsWith('//')) return
    if (path === '/' || path.toLowerCase().startsWith('/login')) return
    if (path.toLowerCase().startsWith('/recover')) return

    try {
        sessionStorage.setItem(INTENDED, path)
    } catch (e) {
        // Private browsing can refuse to write. Losing the redirect is a small
        // inconvenience; throwing here would break the login redirect itself.
    }
}

export function takeIntendedPath() {
    try {
        const path = sessionStorage.getItem(INTENDED)
        sessionStorage.removeItem(INTENDED)
        return path && path.startsWith('/') && !path.startsWith('//') ? path : null
    } catch (e) {
        return null
    }
}
